import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardBody, Button, Input } from '@img/ui';
import { api, type ApiSuccess } from '../lib/api';

interface InitResult {
  transactionId: string;
  randomCode?: string;
  sandbox?: boolean;
  alreadyVerified?: boolean;
  message?: string;
}

interface StatusResult {
  status: 'PENDING' | 'COMPLETED' | 'EXPIRED' | 'REJECTED';
  verified: boolean;
  sandbox?: boolean;
  verifiedAt?: string | null;
}

/**
 * 🪪 صفحة التحقق عبر نفاذ — للمعلنين فقط
 *
 *  ١. المستخدم يدخل رقم الهوية → نبدأ طلب نفاذ
 *  ٢. نعرض رقم عشوائي ٢-خانة (يطلع بتطبيق نفاذ)
 *  ٣. polling كل 3 ثواني لمعرفة نتيجة المصادقة
 *  ٤. عند النجاح، نوجّه المستخدم لصفحة نشر الإعلان
 */
export function NafathVerifyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/submit-property';

  // 🔍 حالة التحقق الحالية
  const { data: meStatus, refetch: refetchMe } = useQuery({
    queryKey: ['nafath', 'me'],
    queryFn: () => api.get<ApiSuccess<{ verified: boolean; verifiedAt: string | null; nationalIdMasked: string | null }>>('/api/nafath/me'),
  });
  const alreadyVerified = meStatus?.data.verified;

  const [nationalId, setNationalId] = useState('');
  const [err, setErr] = useState('');
  const [session, setSession] = useState<InitResult | null>(null);
  const [polling, setPolling] = useState<StatusResult | null>(null);

  const initM = useMutation({
    mutationFn: () => api.post<ApiSuccess<InitResult>>('/api/nafath/initiate', { nationalId }),
    onSuccess: (r) => {
      setErr('');
      setSession(r.data);
      if (r.data.alreadyVerified) refetchMe();
    },
    onError: (e: Error) => setErr(e.message),
  });

  // 🔁 Polling
  useEffect(() => {
    if (!session?.transactionId || polling?.status === 'COMPLETED') return;
    const interval = setInterval(async () => {
      try {
        const r = await api.get<ApiSuccess<StatusResult>>(`/api/nafath/status/${session.transactionId}`);
        setPolling(r.data);
        if (r.data.status === 'COMPLETED') {
          clearInterval(interval);
          refetchMe();
          setTimeout(() => navigate(next), 1500);
        }
      } catch { /* صامت */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [session?.transactionId, polling?.status, navigate, next, refetchMe]);

  // ✅ مُتحقَّق مسبقاً
  if (alreadyVerified) {
    return (
      <div className="bg-gradient-to-b from-navy-soft/30 to-white min-h-[70vh] py-12 px-4">
        <Card className="max-w-md mx-auto"><CardBody className="text-center py-8">
          <div className="text-6xl mb-3">✅</div>
          <h1 className="text-2xl font-black text-navy-dark mb-2">مُتحقَّق بنفاذ</h1>
          <p className="text-sm text-gray-600 mb-3">
            تم التحقق من هويتك بنجاح
            {meStatus?.data.nationalIdMasked && <> — <span dir="ltr" className="font-mono">{meStatus.data.nationalIdMasked}</span></>}
          </p>
          {meStatus?.data.verifiedAt && <p className="text-xs text-gray-500 mb-5">{new Date(meStatus.data.verifiedAt).toLocaleString('ar-SA')}</p>}
          <Button variant="primary" className="w-full" onClick={() => navigate(next)}>متابعة لنشر الإعلان</Button>
        </CardBody></Card>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-navy-soft/30 to-white min-h-[80vh]">
      <section className="bg-gradient-to-l from-navy-darker via-navy-dark to-navy text-white py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-6xl mb-3">🪪</div>
          <h1 className="text-3xl font-black mb-2">التحقق عبر نفاذ الوطني</h1>
          <p className="text-gold-light text-sm leading-relaxed max-w-xl mx-auto">
            للنشر كمُعلِن عقاري، يلزم التحقق من هويتك عبر <b>نفاذ</b> — نظام التوثيق الوطني المعتمد.
            هذا متطلب الهيئة العامة للعقار (REGA).
          </p>
        </div>
      </section>

      <div className="max-w-md mx-auto px-4 py-10 space-y-4">
        {!session ? (
          // ━━━ خطوة ١: إدخال الهوية ━━━
          <Card><CardBody className="space-y-3">
            <h2 className="font-black text-navy-dark">١. أدخل رقم هويتك الوطنية</h2>
            <Input
              label="رقم الهوية"
              value={nationalId}
              onChange={(e) => setNationalId(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="١٠ أرقام تبدأ بـ ١ أو ٢"
              dir="ltr"
              type="text"
              inputMode="numeric"
            />
            {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
            <Button variant="primary" className="w-full" loading={initM.isPending} onClick={() => initM.mutate()} disabled={nationalId.length !== 10}>
              متابعة لنفاذ ←
            </Button>
            <p className="text-xs text-gray-500 leading-relaxed">
              بياناتك محفوظة بسرّية. لن يطّلع عليها أحد، وتُستخدم فقط للتحقق وفق
              نظام حماية البيانات الشخصية (PDPL).
            </p>
          </CardBody></Card>
        ) : polling?.status === 'COMPLETED' ? (
          // ━━━ نجاح ━━━
          <Card><CardBody className="text-center py-8">
            <div className="text-6xl mb-3">✅</div>
            <h2 className="text-2xl font-black text-navy-dark mb-2">تم التحقق بنجاح</h2>
            <p className="text-gray-600 mb-3">يتم تحويلك لإكمال إعلانك…</p>
            <div className="animate-pulse text-gold">⟳ جاري التوجيه</div>
          </CardBody></Card>
        ) : (
          // ━━━ خطوة ٢: انتظار التأكيد من تطبيق نفاذ ━━━
          <Card><CardBody className="space-y-4">
            <div className="text-center">
              <h2 className="font-black text-navy-dark mb-3">٢. افتح تطبيق نفاذ على جوالك</h2>
              <p className="text-sm text-gray-600 mb-4">واضغط على الرقم التالي ضمن قائمة الطلبات:</p>
              {session.randomCode && (
                <div className="inline-block bg-gradient-to-br from-navy-dark to-navy text-gold-light rounded-2xl p-8 shadow-2xl mb-4">
                  <div className="text-7xl font-black tracking-widest" style={{ fontFamily: 'monospace' }}>
                    {session.randomCode}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">سننتظر تأكيدك تلقائياً…</p>
              <div className="mt-3 flex items-center justify-center gap-2 text-gold-dark">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gold-dark border-t-transparent"></div>
                <span className="text-sm font-bold">بانتظار التأكيد من نفاذ</span>
              </div>
            </div>

            {session.sandbox && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                ⚠️ <b>وضع التجربة:</b> لم تُضبَط شهادة نفاذ بعد. سيتم اعتماد التحقق تلقائياً خلال ثواني (للتطوير فقط).
              </div>
            )}

            <Button variant="ghost" className="w-full" onClick={() => { setSession(null); setPolling(null); }}>
              ← الرجوع وإدخال رقم آخر
            </Button>
          </CardBody></Card>
        )}

        <Card><CardBody>
          <details className="text-xs text-gray-600">
            <summary className="font-bold cursor-pointer text-navy">ما هو نفاذ؟</summary>
            <p className="mt-2 leading-relaxed">
              نفاذ الوطني الموحّد هو نظام التوثيق الرسمي السعودي. يربط هوية المستخدم بحسابه على المنصات
              الإلكترونية ويضمن أن المُعلِن العقاري شخص حقيقي قابل للتعريف، وهو شرط من شروط الهيئة العامة
              للعقار لأي معلِن.
            </p>
          </details>
        </CardBody></Card>
      </div>
    </div>
  );
}
